import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { cn } from '@/lib/utils';

interface AdminSectionHeaderProps {
  title: string;
  description: string;
  icon: LucideIcon;
  iconClassName?: string;
  iconBgClassName?: string;
  showLogo?: boolean;
  actions?: React.ReactNode;
  className?: string;
}

const itemVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

export const AdminSectionHeader: React.FC<AdminSectionHeaderProps> = ({
  title,
  description,
  icon: Icon,
  iconClassName = "text-primary",
  iconBgClassName = "bg-primary/10",
  showLogo = true,
  actions,
  className
}) => {
  const { user } = useAuth();
  
  return (
    <motion.div 
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      className={cn("flex flex-col gap-4", className)}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className={cn(
            "p-4 rounded-full flex items-center justify-center",
            iconBgClassName,
            "bg-gradient-to-br from-primary/20 to-primary/10",
            "shadow-sm hover:shadow-md transition-all duration-300",
            "relative overflow-hidden group"
          )}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <Icon className={cn(
              "h-6 w-6 relative z-10 transition-transform duration-300 group-hover:scale-110",
              iconClassName
            )} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              {title}
            </h1>
            <p className="text-muted-foreground">
              {description}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {actions}
          {showLogo && user?.user_metadata?.organization_logo_url && (
            <div className="ml-auto group">
              <OptimizedImage 
                src={user.user_metadata.organization_logo_url} 
                alt="Company Logo" 
                className="h-12 w-12 object-contain rounded-lg shadow-sm hover:shadow-md transition-all duration-300 group-hover:scale-105" 
              />
            </div>
          )}
        </div>
      </div>
      <Separator 
        variant="gradient" 
        className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent"
      />
    </motion.div>
  );
};

export default AdminSectionHeader;
